import type { DecorationSchema } from "./schema/types";
import { isRecord } from "./screen";
import type { Decoration } from "./types";

export const PREVIEW_NS = "inno-preview";

export const PREVIEW_FRAME_NAME = "inno-preview-app";

export interface PreviewReadyMessage {
	ns: typeof PREVIEW_NS;
	type: "ready";
	template?: Decoration | null;
	schema?: DecorationSchema;
	apps?: { id: string; title: string }[];
	app?: string;
}

export interface PreviewAppMessage {
	ns: typeof PREVIEW_NS;
	type: "app";
	app: string;
}

export interface PreviewReloadMessage {
	ns: typeof PREVIEW_NS;
	type: "reload";
}

export interface PreviewDecorationMessage {
	ns: typeof PREVIEW_NS;
	type: "decoration";
	decoration: Decoration | null;
}

export interface PreviewHighlightMessage {
	ns: typeof PREVIEW_NS;
	type: "highlight";
	screen?: string | null;
	element: string | null;
}

export interface PreviewInspectMessage {
	ns: typeof PREVIEW_NS;
	type: "inspect";
	enabled: boolean;
}

export interface PreviewHoverMessage {
	ns: typeof PREVIEW_NS;
	type: "hover";
	screen: string | null;
	element: string | null;
	group?: string | null;
}

export interface PreviewInspectToggleMessage {
	ns: typeof PREVIEW_NS;
	type: "inspect-toggle";
}

export interface PreviewPickMessage {
	ns: typeof PREVIEW_NS;
	type: "pick";
	screen: string | null;
	element: string;
	group?: string | null;
}

export interface PreviewTilesMessage {
	ns: typeof PREVIEW_NS;
	type: "tiles";
	enabled: boolean;
}

export interface PreviewViewportMessage {
	ns: typeof PREVIEW_NS;
	type: "viewport";
	fill: boolean;
}

export interface PreviewPlace {
	id: string;
	title: string;
}

export interface PreviewDemo {
	subjects: string[];
	upsell: string;
	combo: string;
	sections?: Record<string, string>;
	/** Блюда без модификаторов, размеров и комбо — кладутся в корзину кнопкой «+» на карточке */
	plain?: string[];
	/** Блюда с модификаторами или размерами — на их карточке видно больше элементов */
	rich?: string[];
	/** Блюдо без комбо и обязательных добавок — сценарий корзины кладёт его напрямую, с размером по умолчанию */
	cartProduct?: Record<string, unknown>;
}

export interface PreviewFocusMessage {
	ns: typeof PREVIEW_NS;
	type: "focus";
	screen: string;
	element: string;
	place: string | null;
	demo: PreviewDemo;
}

export interface PreviewRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface PreviewFocusRectMessage {
	ns: typeof PREVIEW_NS;
	type: "focus-rect";
	status: "ok" | "missing" | "error";
	rect: PreviewRect | null;
	label: string | null;
	place: PreviewPlace | null;
	places?: PreviewPlace[];
}

export interface PreviewDescribeMessage {
	ns: typeof PREVIEW_NS;
	type: "describe";
	id: number;
}

export interface PreviewElement {
	screen: string | null;
	name: string;
	parent: number | null;
	tag: string;
	rect: PreviewRect;
	covered?: boolean;
	text?: string;
	style: Record<string, string>;
}

export interface PreviewElementsMessage {
	ns: typeof PREVIEW_NS;
	type: "elements";
	id: number;
	viewport: { width: number; height: number };
	elements: PreviewElement[];
}

export interface PreviewCaptureMessage {
	ns: typeof PREVIEW_NS;
	type: "capture";
	id: number;
}

export interface PreviewImageMessage {
	ns: typeof PREVIEW_NS;
	type: "image";
	id: number;
	status: "ok" | "error";
	image: Blob | null;
	width: number;
	height: number;
}

export interface PreviewNavigateMessage {
	ns: typeof PREVIEW_NS;
	type: "navigate";
	id: number;
	place: string;
	product?: string | null;
	app?: string | null;
	demo?: PreviewDemo;
}

export interface PreviewNavigatedMessage {
	ns: typeof PREVIEW_NS;
	type: "navigated";
	id: number;
	status: "ok" | "missing" | "error";
	place: string;
	message?: string;
}

export interface PreviewRequestMessage {
	ns: typeof PREVIEW_NS;
	type: "request";
	id: number;
	method: string;
	url: string;
	body: unknown;
}

export interface PreviewResponseMessage {
	ns: typeof PREVIEW_NS;
	type: "response";
	id: number;
	status: number;
	data: unknown;
}

export interface PreviewSocketMessage {
	ns: typeof PREVIEW_NS;
	type: "socket";
	id: number;
	action: "open" | "send" | "close";
	url?: string;
	data?: string;
}

export interface PreviewSocketDataMessage {
	ns: typeof PREVIEW_NS;
	type: "socket-data";
	id: number;
	data: string;
}

export interface PreviewFontFamily {
	family: string;
	files: { url: string; weight?: number | null; style?: "normal" | "italic" | null }[];
}

export interface PreviewFont {
	main: string | null;
	light?: string | null;
	bold?: string | null;
	weightLight?: number | null;
	weightBold?: number | null;
	fit?: boolean | null;
	scale?: number | null;
	families?: PreviewFontFamily[];
}

export interface PreviewFontMessage {
	ns: typeof PREVIEW_NS;
	type: "font";
	font: PreviewFont | null;
}

export interface PreviewFontFit {
	reference: string;
	verdict: "same" | "fixable" | "breaks";
	worst: number;
	deviations: { metric: string; percent: number }[];
	ratios: Record<"x" | "cap" | "line" | "width", number>;
}

export interface PreviewFontFamilyReport {
	family: string;
	status: "loaded" | "failed";
	ratios?: { x: number; cap: number; line: number; width: number };
}

export interface PreviewFontReportMessage {
	ns: typeof PREVIEW_NS;
	type: "font-report";
	loaded: string[];
	failed: string[];
	app: PreviewFontFit | null;
	families?: PreviewFontFamilyReport[];
}

export interface PreviewRefreshPointMessage {
	ns: typeof PREVIEW_NS;
	type: "refresh-point";
}

export interface PreviewPointMessage {
	ns: typeof PREVIEW_NS;
	type: "point";
	savedAt: string | null;
	refreshFailed?: boolean;
}

export interface PreviewWarmMessage {
	ns: typeof PREVIEW_NS;
	type: "warm";
	urls: string[];
}

export type PreviewMessage =
	| PreviewWarmMessage
	| PreviewRefreshPointMessage
	| PreviewPointMessage
	| PreviewFontMessage
	| PreviewFontReportMessage
	| PreviewReadyMessage
	| PreviewAppMessage
	| PreviewReloadMessage
	| PreviewDecorationMessage
	| PreviewHighlightMessage
	| PreviewInspectMessage
	| PreviewHoverMessage
	| PreviewPickMessage
	| PreviewInspectToggleMessage
	| PreviewTilesMessage
	| PreviewViewportMessage
	| PreviewFocusMessage
	| PreviewFocusRectMessage
	| PreviewDescribeMessage
	| PreviewElementsMessage
	| PreviewCaptureMessage
	| PreviewImageMessage
	| PreviewNavigateMessage
	| PreviewNavigatedMessage
	| PreviewRequestMessage
	| PreviewResponseMessage
	| PreviewSocketMessage
	| PreviewSocketDataMessage;

export const isPreviewMessage = (data: unknown): data is PreviewMessage => isRecord(data) && data.ns === PREVIEW_NS && typeof data.type === "string";

export const isLocalOrigin = (origin: string) => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin);

export const PREVIEW_SNAPSHOT_CACHE_PREFIX = "preview-point-";

export const previewFrameName = (tile: string | null, session: string) => `${PREVIEW_FRAME_NAME}${tile ? `:${tile}` : ""}@${session}`;

export const parsePreviewOrigins = (list: string | undefined) =>
	(list ?? "")
		.split(",")
		.map((origin) => origin.trim())
		.filter(Boolean);

export const isTrustedPreviewOrigin = (origin: string, trusted: readonly string[]) =>
	origin === window.location.origin || (import.meta.env.DEV && isLocalOrigin(origin)) || trusted.includes(origin);
